import React, { useRef, useState, useCallback } from "react";
import { Box, Popover, TextField, Typography, Button, useTheme, } from "@mui/material";
import { Icon } from "@iconify/react";
const DEFAULT_PLACEHOLDER = "Edit Text...";
const DEFAULT_SAVE_LABEL = "Save";
const DEFAULT_CANCEL_LABEL = "Cancel";
const DEFAULT_ENTER_DELAY = 300;
const DEFAULT_LEAVE_DELAY = 400;
/**
 * Info icon that shows a dark tooltip on hover. When editable, clicking the pencil
 * or the tooltip text switches to edit mode (textarea + Save/Cancel).
 */
export const EditableInfoTooltip = ({ value: valueProp, defaultValue = "", onSave, onChange, placeholder = DEFAULT_PLACEHOLDER, editable: editableProp, saveLabel = DEFAULT_SAVE_LABEL, cancelLabel = DEFAULT_CANCEL_LABEL, placement = "bottom-start", enterDelay = DEFAULT_ENTER_DELAY, leaveDelay = DEFAULT_LEAVE_DELAY, minRows = 1, maxRows = 4, iconSx, paperSx, ariaLabel = "Section information", }) => {
    const theme = useTheme();
    const anchorRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editDraft, setEditDraft] = useState("");
    const leaveTimerRef = useRef(null);
    const enterTimerRef = useRef(null);
    const isControlled = valueProp !== undefined;
    const [internalValue, setInternalValue] = useState(defaultValue);
    const value = isControlled ? valueProp !== null && valueProp !== void 0 ? valueProp : "" : internalValue;
    const setValue = useCallback((next) => {
        if (!isControlled)
            setInternalValue(next);
        onChange === null || onChange === void 0 ? void 0 : onChange(next);
    }, [isControlled, onChange]);
    const editable = editableProp !== null && editableProp !== void 0 ? editableProp : !!onSave;
    const displayText = value.trim() || placeholder;
    const clearLeaveTimer = useCallback(() => {
        if (leaveTimerRef.current) {
            clearTimeout(leaveTimerRef.current);
            leaveTimerRef.current = null;
        }
    }, []);
    const clearEnterTimer = useCallback(() => {
        if (enterTimerRef.current) {
            clearTimeout(enterTimerRef.current);
            enterTimerRef.current = null;
        }
    }, []);
    const handleOpen = useCallback(() => {
        clearLeaveTimer();
        clearEnterTimer();
        enterTimerRef.current = setTimeout(() => {
            enterTimerRef.current = null;
            setOpen(true);
        }, enterDelay);
    }, [enterDelay, clearLeaveTimer, clearEnterTimer]);
    const handleClose = useCallback(() => {
        clearEnterTimer();
        leaveTimerRef.current = setTimeout(() => {
            leaveTimerRef.current = null;
            if (!isEditMode)
                setOpen(false);
        }, leaveDelay);
    }, [leaveDelay, isEditMode, clearEnterTimer]);
    const handlePopoverMouseEnter = useCallback(() => {
        clearLeaveTimer();
    }, [clearLeaveTimer]);
    const handlePopoverMouseLeave = useCallback(() => {
        if (!isEditMode)
            handleClose();
    }, [isEditMode, handleClose]);
    const handleEnterEdit = useCallback(() => {
        if (!editable)
            return;
        clearLeaveTimer();
        setEditDraft(value);
        setIsEditMode(true);
    }, [editable, clearLeaveTimer, value]);
    const handleSave = useCallback((e) => {
        e === null || e === void 0 ? void 0 : e.preventDefault();
        e === null || e === void 0 ? void 0 : e.stopPropagation();
        onSave === null || onSave === void 0 ? void 0 : onSave(editDraft);
        if (!isControlled)
            setInternalValue(editDraft);
        onChange === null || onChange === void 0 ? void 0 : onChange(editDraft);
        setIsEditMode(false);
        setOpen(false);
    }, [onSave, editDraft, isControlled, onChange]);
    const handleCancel = useCallback(() => {
        if (isControlled) {
            onChange === null || onChange === void 0 ? void 0 : onChange(valueProp !== null && valueProp !== void 0 ? valueProp : "");
        }
        else {
            setInternalValue(defaultValue);
        }
        setIsEditMode(false);
        setOpen(false);
    }, [isControlled, valueProp, defaultValue, onChange]);
    const handlePopoverClose = useCallback((_, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
            if (isEditMode) {
                if (isControlled)
                    onChange === null || onChange === void 0 ? void 0 : onChange(valueProp !== null && valueProp !== void 0 ? valueProp : "");
                else
                    setInternalValue(defaultValue);
                setIsEditMode(false);
            }
            setOpen(false);
        }
    }, [isEditMode, isControlled, valueProp, defaultValue, onChange]);
    const popoverPaperSx = Object.assign({ bgcolor: theme.palette.grey[800], color: "common.white", borderRadius: 1.5, boxShadow: theme.shadows[8], minWidth: 200, maxWidth: 320 }, paperSx);
    return (React.createElement(React.Fragment, null,
        React.createElement(Box, { ref: anchorRef, component: "span", onMouseEnter: handleOpen, onMouseLeave: handleClose, sx: Object.assign({ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 20, height: 20, borderRadius: "50%", bgcolor: "info.lighter", color: "info.main", ml: 0.5, verticalAlign: "middle", cursor: "pointer" }, iconSx), "aria-label": ariaLabel },
            React.createElement(Icon, { icon: "eva:info-outline", width: 14, height: 14 })),
        React.createElement(Popover, { open: open, anchorEl: anchorRef.current, onClose: handlePopoverClose, anchorOrigin: { vertical: "bottom", horizontal: "center" }, transformOrigin: { vertical: "top", horizontal: "center" }, disableRestoreFocus: true, sx: { pointerEvents: "none" }, slotProps: {
                paper: {
                    sx: Object.assign(Object.assign({}, popoverPaperSx), { pointerEvents: "auto" }),
                    onMouseEnter: handlePopoverMouseEnter,
                    onMouseLeave: handlePopoverMouseLeave,
                },
            } },
            React.createElement(Box, { sx: { pointerEvents: "auto", p: 1 } }, isEditMode ? (React.createElement(React.Fragment, null,
                React.createElement(TextField, { fullWidth: true, multiline: true, minRows: minRows, maxRows: maxRows, value: editDraft, onChange: (e) => setEditDraft(e.target.value), placeholder: placeholder, variant: "standard", size: "small", autoFocus: true, InputProps: {
                        disableUnderline: true,
                        sx: {
                            color: "common.white",
                            "& .MuiInputBase-input": {
                                color: "common.white",
                                "&::placeholder": { opacity: 0.7 },
                            },
                        },
                    }, sx: { mb: 1.5 } }),
                React.createElement(Box, { sx: { display: "flex", gap: 1, justifyContent: "center" } },
                    React.createElement(Button, { type: "button", size: "small", onClick: (e) => handleSave(e), startIcon: React.createElement(Icon, { icon: "eva:checkmark-outline", width: 18, height: 18 }), sx: {
                            color: "common.white",
                            borderColor: "grey.500",
                            "&:hover": { borderColor: "grey.400", bgcolor: "grey.700" },
                        }, variant: "outlined" }, saveLabel),
                    React.createElement(Button, { type: "button", size: "small", onClick: handleCancel, startIcon: React.createElement(Icon, { icon: "mingcute:close-line", width: 18, height: 18 }), sx: {
                            color: "common.white",
                            borderColor: "grey.500",
                            "&:hover": { borderColor: "grey.400", bgcolor: "grey.700" },
                        }, variant: "outlined" }, cancelLabel)))) : (React.createElement(Box, { onClick: handleEnterEdit, sx: {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    cursor: editable ? "pointer" : "default",
                    minHeight: 22,
                } },
                React.createElement(Typography, { variant: "body2", sx: {
                        color: "common.white",
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                    } }, displayText),
                editable && (React.createElement(Box, { component: "span", sx: { color: "common.white", flexShrink: 0, display: "inline-flex" } },
                    React.createElement(Icon, { icon: "solar:pen-outline", width: 18, height: 18 })))))))));
};
export default EditableInfoTooltip;
